import type { ReactNode } from 'react'
import { Crest, CrestWatermark } from '@/components/Logo'
import { BookletValueNote } from '@/components/BookletValueNote'

/**
 * Ramen kring inloggning, kontoskapande och lösenordsåterställning.
 *
 * Blå helskärmsbotten med klubbmärket överst och ett vitt kort med
 * formuläret – samma uppbyggnad som titelbilden i föreningens
 * presentationsmall, så att appen känns igen direkt.
 */
export function AuthShell({
  title,
  intro,
  showValue = false,
  children,
  footer,
}: {
  title: string
  intro?: string
  /**
   * Visar vad häftet är värt.
   *
   * På inloggning och kontoskapande, där frågan "vad får jag för pengarna?"
   * är skälet att fortsätta. Inte på lösenordsåterställning – den som glömt
   * sitt lösenord är redan kund och ska inte säljas till.
   */
  showValue?: boolean
  children: ReactNode
  footer?: ReactNode
}) {
  return (
    <div className="relative flex min-h-dvh flex-col overflow-hidden bg-brand-surface px-4 pt-safe pb-safe">
      {/*
       * Klubbmärken som vattenstämpel, i stället för det gula diagonalband
       * som satt här förut och mot det blå bara lästes som en ljusblå
       * fyrkant.
       *
       * Hela i bild och inte avskurna mot kanten: en halv sköld blir en
       * fläck igen. Opaciteten ligger lågt av samma skäl som bandet var
       * dämpat – det riktiga märket över rubriken ska vara blickfånget,
       * och tre starka märken hade blivit noll.
       */}
      <CrestWatermark width={138} className="top-8 right-4 rotate-[14deg] opacity-[0.16]" />
      <CrestWatermark width={96} className="top-56 left-3 rotate-[-10deg] opacity-[0.13]" />
      <CrestWatermark width={120} className="bottom-10 left-6 rotate-[-16deg] opacity-[0.13]" />

      <div className="relative mx-auto flex w-full max-w-md flex-1 flex-col justify-center py-10">
        <div className="mb-6 flex flex-col items-center text-center">
          <Crest size={78} />
          <h1 className="mt-4 font-display text-2xl font-extrabold text-on-brand text-balance">
            {title}
          </h1>
          <p className="mt-1 font-display text-sm font-bold tracking-[0.16em] text-accent uppercase">
            Lirahäftet
          </p>
          {intro && <p className="mt-3 text-sm text-on-brand/80 text-balance">{intro}</p>}
        </div>

        {/*
          * relative för stämpelns skull.
          *
          * Den sitter snett över kortets övre högra hörn och sticker utanför
          * kanten – därför behöver kortet vara dess positioneringsförälder,
          * och därför får kortet inte ha overflow-hidden.
          */}
        <div className="relative rounded-card border border-white/10 bg-surface p-5 shadow-lift">
          {showValue && <BookletValueNote />}
          {children}
        </div>

        {footer && <div className="mt-5 text-center text-sm text-on-brand/85">{footer}</div>}
      </div>
    </div>
  )
}
